import { supabase } from './supabase';

export async function invokeEdge<T>(
  name: string,
  options: {
    method?: string;
    body?: unknown;
    searchParams?: Record<string, string>;
  } = {}
): Promise<T> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) {
    throw new Error('Not authenticated');
  }

  const base = import.meta.env.VITE_SUPABASE_URL;
  if (!base) {
    throw new Error('Missing VITE_SUPABASE_URL');
  }

  const url = new URL(`${base}/functions/v1/${name}`);
  if (options.searchParams) {
    for (const [k, v] of Object.entries(options.searchParams)) {
      url.searchParams.set(k, v);
    }
  }

  const res = await fetch(url.toString(), {
    method: options.method || 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      'Content-Type': 'application/json',
    },
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = { error: text };
  }

  if (!res.ok) {
    const err = parsed as { error?: string; message?: string };
    throw new Error(err?.error || err?.message || `Request failed (${res.status})`);
  }

  return parsed as T;
}

export async function invokeEdgeBlob(
  name: string,
  options: {
    method?: string;
    searchParams?: Record<string, string>;
  } = {}
): Promise<Blob> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) {
    throw new Error('Not authenticated');
  }

  const base = import.meta.env.VITE_SUPABASE_URL;
  if (!base) {
    throw new Error('Missing VITE_SUPABASE_URL');
  }

  const url = new URL(`${base}/functions/v1/${name}`);
  if (options.searchParams) {
    for (const [k, v] of Object.entries(options.searchParams)) {
      url.searchParams.set(k, v);
    }
  }

  const res = await fetch(url.toString(), {
    method: options.method || 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
  });

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const parsed = await res.json();
      message = parsed.error || parsed.message || message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }

  return res.blob();
}
